import React from "react";

/**
 * Swatch — color chip for chart legends, channel keys, status dots
 * (Phase 2d Rich).
 *
 * Replaces the caller-supplied `style={{ background: rgb(...) }}` escape
 * that the ChartFrame legend currently uses to render channel colors.
 * Swatch is a typed enum over the design-system channel + tone palette:
 * pass a slug, get the corresponding token color. New tokens land in
 * one place when more channels arrive.
 *
 * Tones cover:
 *   - Channels: gtm | partner | self | ae | others | churn
 *   - Status:   accent | good | warn | danger | info
 *   - Neutral:  neutral (text-muted)
 *   - Custom:   pass `color` as a CSS color string (rare, escape hatch).
 *
 * Variants:
 *   - filled (default): solid square in the tone color
 *   - dashed:           dashed border + transparent fill (compare-overlay
 *                       lines in GrowthChart use this convention)
 *
 * Size: 12px by default (matches the legacy .legend-swatch dimensions).
 */

export type SwatchTone =
  | "gtm" | "partner" | "self" | "ae" | "others" | "churn"
  | "accent" | "good" | "warn" | "danger" | "info"
  | "neutral";

export type SwatchVariant = "filled" | "dashed";

export interface SwatchProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
  tone?: SwatchTone;
  variant?: SwatchVariant;
  size?: number;
  /** Escape hatch — overrides tone with a raw color string. Use only when
   *  the swatch represents a non-tokenized color (e.g. a chart series the
   *  user just picked). Prefer adding a token. */
  color?: string;
}

const TONE_VAR: Record<SwatchTone, string> = {
  gtm:     "var(--ch-gtm)",
  partner: "var(--ch-partner)",
  self:    "var(--ch-self)",
  ae:      "var(--ch-ae)",
  others:  "var(--ch-others)",
  churn:   "var(--ch-churn)",
  accent:  "var(--accent)",
  good:    "var(--success)",
  warn:    "var(--warning)",
  danger:  "var(--danger)",
  info:    "var(--info)",
  neutral: "var(--text-muted)",
};

export default function Swatch({
  tone = "neutral",
  variant = "filled",
  size = 12,
  color,
  className = "",
  style,
  ...rest
}: SwatchProps) {
  const swatchColor = color || TONE_VAR[tone];
  const baseStyle: React.CSSProperties = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: 2,
    display: "inline-block",
    ...(variant === "dashed"
      ? { background: "transparent", border: `1.5px dashed ${swatchColor}` }
      : { background: swatchColor }),
    ...style,
  };
  return (
    <span
      className={`v2-swatch v2-swatch--${variant} ${className}`.trim()}
      aria-hidden="true"
      style={baseStyle}
      {...rest}
    />
  );
}
